import { useEffect, useState } from "react";
import { Brain, Info } from "lucide-react";
import { cn } from "@/lib/utils";
import { PREMIUM_PANEL, PanelGlow } from "./_styles";
import { supabase } from "@/integrations/supabase/client";

/**
 * Parent-facing view of the learning engine's per-competency state.
 *
 * Sparse-evidence safety is the whole point of this card: the engine only
 * claims something about a teen when it has enough valid, independent
 * evidence. Until then the parent reads "too early to say", never a number
 * dressed up as a conclusion.
 */
export interface ParentRecentIntervention {
  /** "infographic" | "video" | a text-support modality. */
  type: string | null;
  delivered_at: string | null;
  /** Delivered is not the same as helped: this stays pending until independent evidence exists. */
  outcome: "improved_afterwards" | "no_change_yet" | "awaiting_independent_check";
}

export interface ParentCompetencySignal {
  competency: string;
  evidence_strength: "strong" | "limited" | "sparse";
  signal: string;
  mastery: number | null;
  retention: number | null;
  transfer: number | null;
  last_evidence_at: string | null;
  reported_trend?: string | null;
  retention_status?: string | null;
  transfer_status?: string | null;
  needs_confirmation?: boolean | null;
  recent_intervention?: ParentRecentIntervention | null;
}

export interface ParentCurrentFocus {
  competency_key: string | null;
  next_action: string | null;
  decided_at: string | null;
}

export interface ParentSignalsEnvelope {
  observed_competencies: number;
  sparse_evidence: boolean;
  current_focus?: ParentCurrentFocus | null;
  signals: ParentCompetencySignal[];
}

/** Parent wording for the delivered-vs-helped distinction. */
const INTERVENTION_LABEL: Record<string, string> = {
  infographic: "HeirWise showed a visual to explain this",
  video: "HeirWise played a short explanation for this",
  ai_study_coach_text: "HeirWise gave a written explanation for this",
  static_text_fallback: "HeirWise gave a written explanation for this",
  lumi_support: "HeirWise offered extra guidance for this",
};

const OUTCOME_LABEL: Record<string, string> = {
  improved_afterwards: "Their next independent check improved.",
  no_change_yet: "Their next independent check hasn't improved yet.",
  awaiting_independent_check: "We'll know if it helped after the next independent check.",
};

const RETENTION_LABEL: Record<string, string> = {
  recheck_scheduled: "A later re-check is scheduled.",
  held_over_time: "Still there when it came back later.",
};

const TRANSFER_LABEL: Record<string, string> = {
  new_situations_still_hard: "New situations are still harder than practised ones.",
  applies_in_new_situations: "Used it correctly in a new situation.",
};

/** Plain-English wording for every engine signal. No jargon reaches a parent. */
export const SIGNAL_COPY: Record<string, { label: string; detail: string; tone: string }> = {
  not_yet_assessed: {
    label: "Not started yet",
    detail: "No lesson evidence for this skill yet.",
    tone: "text-muted-foreground",
  },
  too_early_to_say: {
    label: "Too early to say",
    detail: "We have a little evidence, but not enough to draw a conclusion.",
    tone: "text-muted-foreground",
  },
  needs_a_check_in: {
    label: "Needs a check-in",
    detail: "Recent results disagree, so we're re-checking before we say more.",
    tone: "text-amber-500",
  },
  solid: {
    label: "Solid",
    detail: "Consistently correct on their own, across sessions.",
    tone: "text-emerald-500",
  },
  fading_between_sessions: {
    label: "Fading between sessions",
    detail: "Strong in the moment, weaker when it comes back later.",
    tone: "text-amber-500",
  },
  struggles_in_new_situations: {
    label: "Harder in new situations",
    detail: "Knows the practiced version; new framings are still tricky.",
    tone: "text-amber-500",
  },
  needs_support: {
    label: "Needs support",
    detail: "We're adding extra practice and explanation here.",
    tone: "text-rose-500",
  },
  building: {
    label: "Building",
    detail: "Progressing steadily with normal practice.",
    tone: "text-primary",
  },
};

export function signalCopy(signal: string) {
  return (
    SIGNAL_COPY[signal] ?? {
      label: "In progress",
      detail: "We're still gathering evidence for this skill.",
      tone: "text-muted-foreground",
    }
  );
}

interface Props {
  childId: string | null;
  childName?: string | null;
}

export default function CompetencySignalsCard({ childId, childName }: Props) {
  const [data, setData] = useState<ParentSignalsEnvelope | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!childId) {
      setData(null);
      return;
    }
    setLoading(true);
    setError(false);
    (async () => {
      const { data: raw, error: rpcError } = await (supabase as never as {
        rpc: (n: string, a: unknown) => Promise<{ data: unknown; error: unknown }>;
      }).rpc("get_parent_competency_signals", { p_learner_id: childId });
      if (cancelled) return;
      setLoading(false);
      if (rpcError || !raw) {
        setError(true);
        return;
      }
      setData(raw as ParentSignalsEnvelope);
    })();
    return () => {
      cancelled = true;
    };
  }, [childId]);

  if (!childId) return null;

  const who = childName ? `${childName}'s` : "Your teen's";

  return (
    <section className={cn(PREMIUM_PANEL, "p-5")}>
      <PanelGlow />
      <div className="relative space-y-4">
        <div className="flex items-center gap-2.5">
          <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-primary/10 text-primary ring-1 ring-primary/20">
            <Brain className="h-4 w-4" />
          </span>
          <div className="min-w-0">
            <p className="font-heading text-sm font-semibold text-foreground">{who} skill signals</p>
            <p className="text-[11px] text-muted-foreground">
              What the learning engine has actually observed — not a grade.
            </p>
          </div>
        </div>

        {loading && <p className="text-xs text-muted-foreground">Loading skill signals…</p>}

        {error && (
          <p className="text-xs text-muted-foreground">
            Skill signals aren't available right now. Everything else on this page is unaffected.
          </p>
        )}

        {!loading && !error && data && data.signals.length === 0 && (
          <div className="rounded-xl border border-border/60 bg-muted/20 p-3">
            <p className="text-xs text-foreground/85">
              No skill signals yet. They appear after the first few lessons.
            </p>
          </div>
        )}

        {!loading && !error && data && data.sparse_evidence && data.signals.length > 0 && (
          <div className="flex items-start gap-2 rounded-xl border border-amber-500/30 bg-amber-500/5 p-3">
            <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-500" />
            <p className="text-[11px] leading-relaxed text-foreground/85">
              Early days — this is based on very little evidence so far. Treat it as a first
              impression, not a verdict.
            </p>
          </div>
        )}

        {!loading && !error && data?.current_focus?.next_action && (
          <div className="rounded-xl border border-primary/20 bg-primary/5 p-3">
            <p className="text-[11px] font-semibold text-primary">Working on right now</p>
            <p className="mt-0.5 text-[11px] leading-relaxed text-foreground/85">
              {data.current_focus.next_action}
            </p>
          </div>
        )}

        {!loading && !error && data && data.signals.length > 0 && (
          <ul className="space-y-2">
            {data.signals.slice(0, 8).map((s) => {
              const copy = signalCopy(s.signal);
              return (
                <li
                  key={s.competency}
                  className="rounded-xl border border-border/60 bg-background/40 p-3"
                >
                  <div className="flex items-center justify-between gap-3">
                    <p className="min-w-0 truncate font-heading text-xs font-semibold text-foreground">
                      {s.competency}
                    </p>
                    <span className={cn("shrink-0 text-[11px] font-semibold", copy.tone)}>
                      {copy.label}
                    </span>
                  </div>
                  <p className="mt-1 text-[11px] leading-relaxed text-muted-foreground">
                    {copy.detail}
                    {s.evidence_strength === "sparse" && " Based on limited evidence so far."}
                  </p>
                  {(s.retention_status && RETENTION_LABEL[s.retention_status]) && (
                    <p className="mt-1 text-[11px] leading-relaxed text-muted-foreground">
                      {RETENTION_LABEL[s.retention_status]}
                    </p>
                  )}
                  {(s.transfer_status && TRANSFER_LABEL[s.transfer_status]) && (
                    <p className="mt-1 text-[11px] leading-relaxed text-muted-foreground">
                      {TRANSFER_LABEL[s.transfer_status]}
                    </p>
                  )}
                  {s.recent_intervention?.type && (
                    <p className="mt-1.5 rounded-lg bg-muted/30 px-2 py-1.5 text-[11px] leading-relaxed text-foreground/80">
                      {INTERVENTION_LABEL[s.recent_intervention.type] ?? "HeirWise gave extra support for this"}
                      {" — "}
                      {OUTCOME_LABEL[s.recent_intervention.outcome]}
                    </p>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </section>
  );
}
